import { inject, injectable } from 'inversify'
import { action, computed, observable, makeObservable, runInAction } from 'mobx'
import { Types } from '@core/ioc/types'
import type { AppLifecycle } from '@core/interfaces/app-lifecycle'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import { HttpGateway } from '@core/services/http.gateway'
import type { ClockGateway } from '@core/services/clock.gateway'
import { LocalStorageGateway } from '@core/services/local-storage.gateway'
import { z } from '@core/validation/validation.schema'
import { OperationRepository } from '@core/operations/operation.repository'
import type { OperationHandle, OperationOwner } from '@core/operations/operation.types'
import {
  defineOperation,
  operationFailed,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'
import type { ActionFailurePm } from '@core/feedback/feedback.packing'
import { mapReadFailure } from '@core/services/gateway-failure.helpers'
import { UserModel } from '@core/infra/authentication/user.model'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import { NotificationRepository } from '@core/infra/notifications/notification.repository'

// Same interval order as PresenceRepository's poll — an in-app notification
// arriving up to a minute late is an acceptable approximation; no
// websocket/SSE infrastructure exists in this app.
const INBOX_POLL_INTERVAL_MS = 60_000

type InboxCheckFailure = ActionFailurePm

// Kept local: the shared schema only carries `resourceType` as a free-form
// string for its generic inbox item (frontend/backend agree on no fixed set
// of resource kinds) — the grant DTO here still validates against this
// closed list of the shapes this app's own inbox source ever produced.
type ResourceType = 'asset' | 'book' | 'workspace' | 'job' | 'procedure'

export type GrantInboxItemPm = {
  readonly kind: 'grant'
  readonly itemId: string
  readonly grantId: string
  readonly resourceType: ResourceType
  readonly resourceId: string
  readonly resourceLabel: string
  readonly grantedBy: string
  readonly grantedAt: string
}

export type ReminderInboxItemPm = {
  readonly kind: 'reminder'
  readonly itemId: string
  readonly jobId: string
  readonly jobTitle: string
  readonly dueOn: string
  readonly daysUntilDue: number
}

/** A discriminated union since Phase 4 — see shared/src/schemas.ts's inboxItemSchema for the wire shape this mirrors. */
export type InboxItemPm = GrantInboxItemPm | ReminderInboxItemPm

const GrantInboxItemDtoSchema = z.object({
  kind: z.literal('grant'),
  itemId: z.string(),
  grantId: z.string(),
  resourceType: z.enum(['asset', 'book', 'workspace', 'job', 'procedure']),
  resourceId: z.string(),
  resourceLabel: z.string(),
  grantedBy: z.string(),
  grantedAt: z.string(),
  permissions: z.array(z.enum(['read', 'add', 'update', 'verify', 'delete', 'manage'])),
})

const ReminderInboxItemDtoSchema = z.object({
  kind: z.literal('reminder'),
  itemId: z.string(),
  jobId: z.string(),
  jobTitle: z.string(),
  dueOn: z.string(),
  daysUntilDue: z.number(),
})

const InboxItemDtoSchema = z.discriminatedUnion('kind', [GrantInboxItemDtoSchema, ReminderInboxItemDtoSchema])
const InboxListDtoSchema = z.array(InboxItemDtoSchema)

export const INBOX_CHECK_OPERATION = defineOperation<null, InboxCheckFailure>({
  id: operationId('inbox.check'),
  key: () => operationKey('current'),
  groups: () => [operationGroup('session'), operationGroup('inbox')],
  pending: { kind: 'ignore' },
  safety: { kind: 'read' },
  timeout: { kind: 'after', milliseconds: 10_000, toFailure: () => ({ code: 'temporarily-unavailable' }) },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

const storageKeyFor = (email: string): string => `inbox-last-seen:${email}`

/**
 * What's been shared with the viewer and not yet opened — the notification
 * half of ShareTargetsPresenter's drag-drop share flow, GET /inbox. Same
 * shape as PresenceRepository: an AppLifecycle participant that starts its
 * own poll, guarded by `pollingStarted`.
 *
 * Two independent "seen" concepts, both driven off `grantedAt`:
 *  - `lastSeenAtPm` — persisted per account through LocalStorageGateway
 *    (deliberately per-device, not per-account server-side: no `User` field
 *    exists for it, and a v8 migration is not worth it for this MVP slice).
 *    Drives the badge count and survives a reload.
 *  - `notifiedThroughAt` — in-memory only, this session's own high-water
 *    mark. Drives which items raise a toast: everything already on the list
 *    the first time a poll succeeds is a backlog, not news, so only items
 *    newer than the previous poll ever notify.
 */
@injectable()
export class InboxRepository implements AppLifecycle, UserScopedState {
  inboxListPm: readonly InboxItemPm[] | null = null
  lastSeenAtPm: string | null = null

  @inject(Types.IClockGateway)
  private clockGateway!: ClockGateway

  @inject(UserModel)
  private userModel!: UserModel

  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  @inject(NotificationRepository)
  private notificationRepository!: NotificationRepository

  @inject(Types.ILocalStorageGateway)
  private localStorageGateway!: LocalStorageGateway

  private readonly dataGateway: HttpGateway
  private readonly operationOwner: OperationOwner
  private readonly checkOperation: OperationHandle<null, InboxCheckFailure>

  private pollingStarted = false
  private lastSeenLoadedForEmail: string | null = null
  private notifiedThroughAt: string | null = null
  private readonly notifiedReminderItemIds = new Set<string>()

  constructor(
    @inject(Types.IDataGateway) dataGateway: HttpGateway,
    @inject(OperationRepository) operationRepository: OperationRepository,
  ) {
    this.dataGateway = dataGateway
    this.operationOwner = operationRepository.createOwner({ id: 'inbox.repository', lifetime: 'session' })
    this.checkOperation = this.operationOwner.register(INBOX_CHECK_OPERATION)

    makeObservable(this, {
      inboxListPm: observable.ref,
      lastSeenAtPm: observable.ref,
      unreadCount: computed,
      checkNow: action,
      markSeen: action,
    })
  }

  initialize(): void {
    if (this.pollingStarted) return
    this.pollingStarted = true

    void this.checkNow()
    this.clockGateway.everyInterval(INBOX_POLL_INTERVAL_MS, () => {
      void this.checkNow()
    })
  }

  /**
   * Grant rows only — the watermark this drives (`lastSeenAtPm`) is keyed off
   * `grantedAt`, a concept reminder rows don't have. A reminder row is never
   * "unread" the way a share is: it's a live derivation, always current, not
   * an event that happened once. It still always renders in the drawer —
   * see `checkNow`'s toast handling below for how a reminder notifies once.
   */
  get unreadCount(): number {
    const items = (this.inboxListPm ?? []).filter((item): item is GrantInboxItemPm => item.kind === 'grant')
    if (this.lastSeenAtPm === null) return items.length
    return items.filter((item) => item.grantedAt > this.lastSeenAtPm!).length
  }

  markSeen = (): void => {
    const email = this.userModel.email
    if (!email) return
    const now = this.clockGateway.now().toISOString()
    this.localStorageGateway.setValue(storageKeyFor(email), now)
    this.lastSeenAtPm = now
  }

  checkNow = async (): Promise<void> => {
    // An inbox is per-account by definition — nothing to poll before a
    // session exists, including on the login page where this AppLifecycle
    // participant's own poll is already ticking. Without this, every tick
    // 401s, which HttpGateway turns into a refresh attempt that then fails
    // and raises "session expired" for someone who was never signed in.
    if (!this.userModel.token) return

    const email = this.userModel.email

    await this.checkOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.get('/inbox', { signal, idempotencyKey: null })
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = InboxListDtoSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data', retryable: false })

        return operationSucceeded(
          Object.freeze(
            parsed.data.map((dto): InboxItemPm => {
              if (dto.kind === 'grant') {
                return Object.freeze({
                  kind: 'grant',
                  itemId: dto.itemId,
                  grantId: dto.grantId,
                  resourceType: dto.resourceType,
                  resourceId: dto.resourceId,
                  resourceLabel: dto.resourceLabel,
                  grantedBy: dto.grantedBy,
                  grantedAt: dto.grantedAt,
                })
              }
              return Object.freeze({
                kind: 'reminder',
                itemId: dto.itemId,
                jobId: dto.jobId,
                jobTitle: dto.jobTitle,
                dueOn: dto.dueOn,
                daysUntilDue: dto.daysUntilDue,
              })
            }),
          ),
        )
      },
      {
        succeeded: (items) => {
          if (email && this.lastSeenLoadedForEmail !== email) {
            this.lastSeenLoadedForEmail = email
            const raw = this.localStorageGateway.getValue(storageKeyFor(email))
            this.lastSeenAtPm = typeof raw === 'string' ? raw : null
          }

          const grantItems = items.filter((item): item is GrantInboxItemPm => item.kind === 'grant')
          const reminderItems = items.filter((item): item is ReminderInboxItemPm => item.kind === 'reminder')

          if (this.notifiedThroughAt !== null) {
            for (const item of grantItems) {
              if (item.grantedAt > this.notifiedThroughAt) {
                this.notificationRepository.notify(
                  'info',
                  this.i18nRepository.t('inbox.notification', {
                    grantedBy: item.grantedBy,
                    resourceLabel: item.resourceLabel,
                  }),
                )
              }
            }
          }
          // Sorted newest-first by the backend — grantItems[0] is the new high-water mark.
          this.notifiedThroughAt = grantItems[0]?.grantedAt ?? this.notifiedThroughAt

          // A reminder row has no "arrival" moment the way a grant does — it's
          // a live derivation of the caller's own due-soon jobs, present every
          // poll it still qualifies. Notify once per (job, dueOn) this
          // session, tracked in memory, never persisted — a reschedule
          // changes dueOn and so gets its own itemId, correctly re-notifying.
          for (const item of reminderItems) {
            if (!this.notifiedReminderItemIds.has(item.itemId)) {
              this.notifiedReminderItemIds.add(item.itemId)
              this.notificationRepository.notify(
                'info',
                this.i18nRepository.t('inbox.reminder.notification', {
                  jobTitle: item.jobTitle,
                  dueOn: item.dueOn,
                }),
              )
            }
          }

          this.inboxListPm = items
        },
        // A failed/timed-out check just means "couldn't tell, stay quiet" —
        // the next scheduled tick is the next attempt, same as PresenceRepository.
        failed: null,
      },
    )
  }

  resetUserScopedState = (): void => {
    this.operationOwner.resetAll({ kind: 'session-reset' })
    runInAction(() => {
      this.inboxListPm = null
      this.lastSeenAtPm = null
    })
    this.lastSeenLoadedForEmail = null
    this.notifiedThroughAt = null
    this.notifiedReminderItemIds.clear()
  }
}
